import { Network } from '@capacitor/network';
import { PromiseExtended, Table } from 'dexie';
import { toast } from 'react-toastify';
import {
  Days,
  EventNames,
  fetchSelectedLessonPlan,
  fetchSelectedFluidLessonPlan,
  getMediaStatus,
  UserRole,
} from '..';
import { analyticsService } from '../api/analytics';
import { migrations } from '../migrations/lessonplan.migration';
import {
  DownloadInfo,
  DownloadRequest,
  DownloadStatus,
  ModelDownloadName,
} from '../types/downloads';
import {
  IBookChapter,
  IGrades,
  IGradeSubject,
  ILessonPlan,
  ILessonPlanDetail,
  ILessonPlanStatus,
  ILessonPlanStep,
  IMedia,
  ISchoolClass,
  ISchoolClassInfo,
  ISchoolClassSubject,
  ISchoolClassTimeTable,
  ISession,
  ISubject,
  StepData,
  TextBody,
  IFluidLessonPlanDetail,
} from '../types/lessonplan';
import { SyncChanges, SyncRecord, SyncRecordStatus } from '../types/sync-state';
import { downloadManagerService } from '../utils/download-manager';
import { userCache } from '../utils/user-cache';
import { BaseModelForPush } from './base-model';
import { downloadRequestModel } from './downloads';
import { FeatureFlagNames, userDB } from './user';

export interface LessonPlanRecord extends ILessonPlan, SyncRecord {
  title: string;
}
export interface lessonPlanDetailRecord {
  [key: string]: any;
}

interface LessonPlanDownloadContext {
  lessonId: number;
  chapterId: number;
  gradeSubject: number;
  chapterTitle: string;
  isFluid?: boolean;
  fluidLpId?: number;
}

interface LessonPlanUpdateResult {
  downloadStatus: DownloadStatus;
  downloadProgress: number;
  downloadErrorCode: DownloadStatus;
}
export interface FluidLessonPlanDetailRecord
  extends IFluidLessonPlanDetail, SyncRecord {}
export interface BookChapterRecord extends IBookChapter, SyncRecord {}
export interface GradeSubjectRecord extends IGradeSubject, SyncRecord {}
export interface SchoolClassRecord extends ISchoolClass, SyncRecord {}
export interface lessonPlanDetailRecord extends ILessonPlanDetail, SyncRecord {}
export interface LessonPlanStatusRecord extends ILessonPlanStatus, SyncRecord {}
export interface LessonPlanGradeRecord extends IGrades, SyncRecord {}
export interface LessonPlanSubjectRecord extends ISubject, SyncRecord {}
export interface SessionRecord extends ISession, SyncRecord {}
export interface LessonPlanTimetableRecord
  extends ISchoolClassTimeTable, SyncRecord {
  deletionBlocked?: boolean;
}

export interface SchoolClassSubjectRecord
  extends ISchoolClassSubject, SyncRecord {}

export class LessonPlanDB extends BaseModelForPush {
  lessonPlans!: Table<LessonPlanRecord>;
  chapters!: Table<BookChapterRecord>;
  gradeSubjects!: Table<GradeSubjectRecord>;
  schoolClasses!: Table<SchoolClassRecord>;
  schoolClassSubjects!: Table<SchoolClassSubjectRecord>;
  lessonPlanDetail!: Table<lessonPlanDetailRecord>;
  fluidLessonPlanDetail!: Table<FluidLessonPlanDetailRecord>;
  lessonPlanStatus!: Table<LessonPlanStatusRecord>;
  grades!: Table<LessonPlanGradeRecord>;
  subjects!: Table<LessonPlanSubjectRecord>;
  sessions!: Table<SessionRecord>;
  schoolClassTimetables!: Table<LessonPlanTimetableRecord>;

  constructor() {
    super('lessonplandb');

    migrations.forEach(({ version, schema, upgrade }) => {
      const dbVersion = this.version(version).stores(schema);
      if (upgrade) {
        dbVersion.upgrade(upgrade);
      }
    });

    this.setupTableHooks(this.tables);
    this.getDownloadData();
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  getSelectedBookChapter(
    chapterId: number,
  ): PromiseExtended<BookChapterRecord[]> {
    return this.chapters
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id === chapterId,
      )
      .sortBy('chapterNumber');
  }

  getSelectedGradeSubject(
    gradeSubjectId: number,
  ): PromiseExtended<GradeSubjectRecord[]> {
    return this.gradeSubjects
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id === gradeSubjectId,
      )
      .sortBy('id');
  }

  getSelectedGradeSubjectByLabels(
    gradeLabel: string,
    subjectLabel: string,
  ): PromiseExtended<GradeSubjectRecord[]> {
    return this.gradeSubjects
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.subjectLabel === subjectLabel &&
          record.gradeLabel === gradeLabel,
      )
      .sortBy('id');
  }
  // update for chapter with download count
  async getChapterByGradeSubjectId(id: number): Promise<BookChapterRecord[]> {
    const [chapters, totalLp, downloadedLp, downloadFailedLp, downloadingLp] =
      await Promise.all([
        this.chapters
          .filter(
            (record) =>
              record.syncStatus !== SyncRecordStatus.Deleted &&
              record.gradeSubject === id,
          )
          .sortBy('chapterNumber'),
        this.lessonPlans
          .filter((lesson) =>
            lesson.bookInfo.some((info) => info.gradeSubject === id),
          )
          .toArray(),
        this.lessonPlanDetail
          .filter(
            (chapter) => chapter.downLoadStatus === DownloadStatus.Completed,
          )
          .toArray(),
        this.lessonPlanDetail
          .filter((chapter) => chapter.downLoadStatus === DownloadStatus.Failed)
          .toArray(),
        this.lessonPlanDetail
          .filter(
            (chapter) => chapter.downLoadStatus === DownloadStatus.Downloading,
          )
          .toArray(),
      ]);
    const downloadedLpSet = new Set(downloadedLp.map((lesson) => lesson.id));
    const failedLessonsMap = new Map(
      downloadFailedLp.map((lesson) => [lesson.id, lesson.downloadErrorCode]),
    );
    const downloadingLpSet = new Set(downloadingLp.map((lesson) => lesson.id));
    const downloadFailedLpSet = new Set(
      downloadFailedLp.map((lesson) => lesson.id),
    );

    return chapters.map((chapter) => {
      const lp = totalLp.filter((lesson) =>
        lesson.bookInfo.some((info) => info.bookChapter === chapter.id),
      );
      const downloadedLpCount = lp.filter((lesson) =>
        downloadedLpSet.has(lesson.id),
      ).length;
      const isDownloading = lp.some((lesson) =>
        downloadingLpSet.has(lesson.id),
      );
      const isFailed = lp.some((lesson) => downloadFailedLpSet.has(lesson.id));
      const failedLesson = lp.find((lesson) => failedLessonsMap.has(lesson.id));

      return {
        ...chapter,
        downloaded: downloadedLpCount,
        downloadTotal: lp.length,
        downloadStatus: this.getDownloadStatus(
          downloadedLpCount,
          lp.length,
          isDownloading,
          isFailed,
        ),
        downloadErrorCode: isFailed
          ? failedLessonsMap.get(failedLesson?.id!)
          : DownloadStatus.None,
      };
    });
  }
  getDownloadStatus = (
    lpDownloadcount: number,
    lpcount: number,
    isDownloading: boolean,
    isFailed: boolean,
  ) => {
    if (lpcount === 0 && lpDownloadcount === 0) return DownloadStatus.None;
    if (lpDownloadcount === lpcount) return DownloadStatus.Completed;
    if (isDownloading) return DownloadStatus.Downloading;
    if (isFailed) return DownloadStatus.Failed;
    return DownloadStatus.None;
  };

  // download chapter Wise lesson plan

  async startDownloadChater(chapter: IBookChapter) {
    let downloadRequest: DownloadRequest[] = [];
    const lessons = await this.getLessonPlansDownloadWise(chapter.id);
    const downloadedLp = await this.getAllLessonPlanDetail(lessons);
    const downloadedLpSet = new Set(downloadedLp.map((lesson) => lesson.id));
    const lessonPlanIds = lessons
      .filter((lesson) => !downloadedLpSet.has(lesson.id))
      .map((lesson) => lesson.id);
    if (lessonPlanIds.length > 0) {
      await fetchSelectedLessonPlan(lessonPlanIds, null);
    }
    const lessonFetched = await this.getAllLessonPlanDetail(lessons);
    for (const lessonPlanDetails of lessonFetched) {
      const mediaObject =
        lessonPlanDB.getDownloadMediaObject(lessonPlanDetails);
      const lpMediaAssets = Object.values(mediaObject);
      await lessonPlanDB.setDownloading(
        lessonPlanDetails,
        lpMediaAssets.length,
        chapter.id,
      );
      if (lpMediaAssets.length > 0) {
        downloadManagerService.downloadMediaAsset(lpMediaAssets, true);
      }
      downloadRequest = [...downloadRequest, ...lpMediaAssets];
    }
    downloadManagerService.downloadMediaAsset(downloadRequest, true);
  }

  async deleteChapterDownload(chapter: IBookChapter) {
    let downloadRequest: DownloadRequest[] = [];
    await downloadManagerService.getDownloadsFromNative(false);
    const lessons = await this.getLessonPlansDownloadWise(chapter.id);
    const downloadedLp = await this.getAllLessonPlanDetail(lessons);
    for (const lessonPlanDetails of downloadedLp) {
      const mediaObject =
        lessonPlanDB.getDownloadMediaObject(lessonPlanDetails);
      const lpMediaAssets = Object.values(mediaObject);
      lessonPlanDB.deleteLessonPlanMedia(lessonPlanDetails.id);
      await downloadRequestModel.deleteDownloadRequestByModelDownloadRequestId(
        lessonPlanDetails.id,
      );
      downloadRequest = [...downloadRequest, ...lpMediaAssets];
    }
    await downloadManagerService.deleteDownload(downloadRequest);
  }
  async retryDownloadChapter(chapter: IBookChapter) {
    const lessons = await this.getLessonPlansDownloadWise(chapter.id);
    const downloadedLp = await this.getAllLessonPlanDetail(lessons);
    const downloadedFluidLp = await this.getAllFluidLessonPlanDetail(lessons);
    for (const lessonPlanDetails of downloadedLp) {
      if (lessonPlanDetails.downLoadStatus === DownloadStatus.Failed) {
        await this.retryLpDownload(lessonPlanDetails.id, chapter.id);
      }
    }
    for (const fluidLessonPlanDetails of downloadedFluidLp) {
      if (
        fluidLessonPlanDetails.downLoadStatus === DownloadStatus.Failed &&
        fluidLessonPlanDetails.legacyLp
      ) {
        await this.retryFluidLpDownload(
          fluidLessonPlanDetails.legacyLp,
          chapter.id,
        );
      }
    }
  }

  getChaptersByChapterIds(
    chapterIds: number[],
  ): PromiseExtended<BookChapterRecord[]> {
    return this.chapters
      .where('id')
      .anyOf(chapterIds)
      .and((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('chapterNumber');
  }

  getSchoolClassSubjectGrade(): PromiseExtended<ISchoolClassSubject[]> {
    return this.schoolClassSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('gradeSubject');
  }
  async getGradeSubjectClass(): Promise<PromiseExtended> {
    const gradeSubject = await this.gradeSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
    const schoolClassSubject = await this.schoolClassSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('gradeSubject');
    const schoolClass = await this.schoolClasses
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('schoolClass');

    const subjectClasses = gradeSubject
      .map((subject) => {
        const matchedClassSubject = schoolClassSubject.find(
          (classSubjectItem) => classSubjectItem.gradeSubject === subject.id,
        );
        return matchedClassSubject
          ? { ...matchedClassSubject, ...subject }
          : null;
      })
      .filter((subjectClass) => subjectClass !== null);
    if (schoolClassSubject.length === 0) {
      return [];
    }

    const schoolClassData = subjectClasses.map((subjectClass) => {
      const matchedSchoolClass = schoolClass.find(
        (schoolClass) => schoolClass.id === subjectClass?.schoolClass,
      );
      return { ...matchedSchoolClass, ...subjectClass };
    });
    return schoolClassData;
  }

  async getSpecificGradeSubjectClass(schoolClassId: number) {
    const schoolClass = await this.schoolClasses
      .where('id')
      .equals(schoolClassId)
      .first();

    if (!schoolClass) {
      return undefined;
    }
    // the above returns school class record, but we also need the grade label with it
    const grade = await this.grades
      .where('id')
      .equals(schoolClass.grade)
      .first();

    return {
      ...schoolClass,
      gradeLabel: grade?.label,
    };
  }

  getLessonPlans(bookChapter: number): PromiseExtended<LessonPlanRecord[]> {
    return this.lessonPlans
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.bookInfo.some(
            (info) => info.bookChapter.toString() === bookChapter.toString(),
          ),
      )
      .sortBy('id');
  }

  async getLessonPlansDownloadWise(
    bookChapter: number,
  ): Promise<PromiseExtended<LessonPlanRecord[]>> {
    // Filter lesson plans by sync status and book chapter
    const filteredLessonPlans = await this.lessonPlans
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.bookInfo.some(
            (info) => info.bookChapter.toString() === bookChapter.toString(),
          ),
      )
      .toArray();

    const sortedLessonPlans = filteredLessonPlans
      .filter(
        (record) =>
          (record.bookInfo = record.bookInfo.filter(
            (info) => info.bookChapter === bookChapter,
          )),
      )
      .sort((a, b) => a.bookInfo[0].index - b.bookInfo[0].index);

    const isKainatLpEnabled = await userDB.getFeatureFlag(
      FeatureFlagNames.kainatLpsEnabled,
    );

    // Filter lesson plans based on feature flag and external source
    return isKainatLpEnabled?.includes(bookChapter)
      ? sortedLessonPlans.filter((lp) => lp.lpExternalSource === 'KAINAT') ||
          sortedLessonPlans
      : sortedLessonPlans.filter(
          (lp) => lp?.lpExternalSource !== 'KAINAT' || !lp.lpExternalSource,
        );
  }

  async getDownloadingLp(): Promise<lessonPlanDetailRecord[]> {
    return await this.lessonPlanDetail
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record?.downLoadStatus === DownloadStatus.Downloading,
      )
      .toArray();
  }
  async getDownloadingWithPropgressLp(): Promise<lessonPlanDetailRecord[]> {
    return await this.lessonPlanDetail
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record?.downLoadStatus === DownloadStatus.Downloading &&
          record?.downloadProgress! > 0,
      )
      .toArray();
  }

  getGradeSubjects(): PromiseExtended<GradeSubjectRecord[]> {
    return this.gradeSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  getSchoolClasses(): PromiseExtended<SchoolClassRecord[]> {
    return this.schoolClasses
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  getLessonPlanMetaData(
    lessonPlanId: number,
  ): PromiseExtended<LessonPlanRecord[]> {
    return this.lessonPlans
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id.toString() === lessonPlanId.toString(),
      )
      .sortBy('index');
  }

  getLessonPlanDetailCount(lessonPlanId: number): PromiseExtended<number> {
    return this.lessonPlanDetail
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id.toString() === lessonPlanId.toString(),
      )
      .count();
  }

  getLessonPlanDetail(
    lessonPlanId: number,
  ): PromiseExtended<lessonPlanDetailRecord[]> {
    return this.lessonPlanDetail
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id.toString() === lessonPlanId.toString(),
      )
      .sortBy('id');
  }

  getFluidLessonPlanDetail(
    lessonPlanId: number,
    searchByLegacyId = false,
  ): PromiseExtended<FluidLessonPlanDetailRecord[]> {
    return this.fluidLessonPlanDetail
      .filter((record) => {
        if (record.syncStatus === SyncRecordStatus.Deleted) {
          return false;
        }

        if (searchByLegacyId) {
          return record.legacyLp?.toString() === lessonPlanId.toString();
        }

        return record.id.toString() === lessonPlanId.toString();
      })
      .sortBy('id');
  }

  async getFluidLessonPlanDetailWithSync(
    lessonPlanId: number,
    searchByLegacyId = false,
  ): Promise<FluidLessonPlanDetailRecord[]> {
    const lessonPlans = await this.getFluidLessonPlanDetail(
      lessonPlanId,
      searchByLegacyId,
    );

    if (lessonPlans.length > 0) {
      await this.ensureFluidLessonPlanMediaSync(lessonPlans);
      return await this.getFluidLessonPlanDetail(
        lessonPlanId,
        searchByLegacyId,
      );
    }

    return lessonPlans;
  }

  //todo: update to remove deleted record
  getLessonPlanDetailRecord(
    lessonPlanId: number,
  ): PromiseExtended<lessonPlanDetailRecord | undefined> {
    return this.lessonPlanDetail.where('id').equals(lessonPlanId).first();
  }
  getDownloadMediaObject(lp: lessonPlanDetailRecord, isUnique = true) {
    const mediaToDownload: { [key: string]: any[] } = {};

    for (const key in lp) {
      const details = lp[key];
      if (Array.isArray(details)) {
        const filteredMediaArray = details.filter((detail: any) => {
          if (detail?.media?.length) {
            return true;
          }

          if (detail?.textBody?.some((item: TextBody) => item?.media?.length)) {
            return true;
          }
          return false;
        });

        if (filteredMediaArray.length) {
          mediaToDownload[key] = filteredMediaArray;
        }
      }
    }
    const mediaObject: {
      [key: string]: DownloadRequest;
    } = {};
    for (const key in mediaToDownload) {
      mediaToDownload[key].forEach((media: any, stepIndex: number) => {
        if (media?.media?.length) {
          media.media.forEach((m: any, mediaIndex: number) => {
            const slug = `${key}-${stepIndex}-${mediaIndex}`;
            mediaObject[isUnique ? m.link : slug] = {
              id: Number(`${lp.id}${stepIndex}${mediaIndex}`),
              url: m.link,
              download: m.download,
              mediaType: m.type,
              modelDownloadName: ModelDownloadName.LessonDetail,
              modelDownloadRequestId: lp.id,
              timestamp: Date.now(),
            };
          });
        }
        if (media?.textBody?.length) {
          media.textBody.forEach((textItem: TextBody, textIndex: number) => {
            if (textItem?.media?.length) {
              textItem.media.forEach((m: any, mediaIndex: number) => {
                const slug = `${key}-${stepIndex}-${textIndex}-${mediaIndex}`;
                mediaObject[isUnique ? m.link : slug] = {
                  id: Number(`${lp.id}${stepIndex}${textIndex}${mediaIndex}`),
                  url: m.link,
                  download: m.download,
                  mediaType: m.type,
                  modelDownloadName: ModelDownloadName.LessonDetail,
                  modelDownloadRequestId: lp.id,
                  timestamp: Date.now(),
                };
              });
            }
          });
        }
      });
    }

    return mediaObject;
  }

  getFluidLessonPlanDownloadMediaObject(
    fluidLp: FluidLessonPlanDetailRecord,
    isUnique = true,
  ) {
    const mediaObject: { [key: string]: DownloadRequest } = {};

    if (fluidLp.sections) {
      fluidLp.sections.forEach((section, sectionIndex) => {
        if (section.variants) {
          section.variants.forEach((variantWrapper: any, variantIndex) => {
            if (
              variantWrapper.media &&
              Array.isArray(variantWrapper.media) &&
              variantWrapper.media.length > 0
            ) {
              variantWrapper.media.forEach(
                (mediaItem: any, mediaIndex: number) => {
                  const mediaUrl = mediaItem.link;
                  if (mediaUrl) {
                    const key = `section-${sectionIndex}-variant-${variantIndex}-media-${mediaIndex}`;
                    mediaObject[isUnique ? mediaUrl : key] = {
                      id: Number(
                        `${fluidLp.id}${sectionIndex}${variantIndex}${mediaIndex}`,
                      ),
                      url: mediaUrl,
                      download: mediaItem.download,
                      mediaType: mediaItem.type ?? 'image',
                      modelDownloadName: ModelDownloadName.LessonDetail,
                      modelDownloadRequestId: fluidLp.legacyLp!,
                      timestamp: Date.now(),
                    };
                  }
                },
              );
            }
          });
        }
      });
    }

    return mediaObject;
  }

  private extractMediaFromTipTapContent(content: any): any[] {
    const mediaItems: any[] = [];

    if (content && typeof content === 'object') {
      if (content.type === 'image' && content.attrs?.src) {
        const mediaItem: any = {
          type: 'image',
          link: content.attrs.src,
          action: 'show',
        };
        if (content.attrs.download) {
          mediaItem.download = content.attrs.download;
        }
        mediaItems.push(mediaItem);
      } else if (Array.isArray(content.content)) {
        content.content.forEach((node: any) => {
          mediaItems.push(...this.extractMediaFromTipTapContent(node));
        });
      } else if (typeof content === 'object') {
        Object.keys(content).forEach((key) => {
          if (content[key] && typeof content[key] === 'object') {
            mediaItems.push(
              ...this.extractMediaFromTipTapContent(content[key]),
            );
          }
        });
      }
    }

    return mediaItems;
  }

  async updateLessonsAlreadyDownloadedStatus(
    lessonDetails: lessonPlanDetailRecord[],
  ) {
    return this.updateLessonDownloadStatus(lessonDetails, false);
  }

  async updateFluidLessonsAlreadyDownloadedStatus(
    fluidLessonDetails: FluidLessonPlanDetailRecord[],
  ) {
    return this.updateLessonDownloadStatus(fluidLessonDetails, true);
  }

  async retryDownloadLessonPlan(
    lessonPlanDetail: lessonPlanDetailRecord,
    chapterId: number,
  ) {
    const mediaObject = lessonPlanDB.getDownloadMediaObject(lessonPlanDetail);
    const mediaAssetArray = Object.values(mediaObject);
    mediaAssetArray.forEach((data) => {
      data.modelDownloadAddedInformation = {
        chapterId: chapterId,
      };
    });
    if (mediaAssetArray.length) {
      downloadManagerService.downloadMediaAsset(
        Object.values(mediaObject),
        true,
      );
    }
  }
  // remove lesson plan media from the lp details object because on the next download it can be different path.
  async deleteLessonPlanMedia(lpId: number) {
    this.transaction('rw', this.lessonPlanDetail, async () => {
      const lessonDetail = await this.lessonPlanDetail.get(lpId);
      if (!lessonDetail) {
        return;
      }
      delete lessonDetail.downLoadStatus;
      delete lessonDetail.downloadProgress;
      delete lessonDetail.downloadErrorCode;
      for (const key in lessonDetail) {
        if (Array.isArray(lessonDetail[key])) {
          lessonDetail[key].forEach((detail: any) => {
            if (typeof detail === 'object' && detail.media?.length) {
              detail.media.forEach((m: any) => {
                if (m?.download) {
                  delete m.download;
                }
              });
            }
          });
        }
      }
      await this.lessonPlanDetail.put(lessonDetail);
      toast('Removed from downloads', {
        toastId: 'lessonDownloadRemove',
      });
    });
  }

  private cleanFluidLessonVariant(variantWrapper: any): void {
    if (variantWrapper.variant?.content) {
      this.cleanMediaFromContent(variantWrapper.variant.content);
    }

    if (variantWrapper.media && Array.isArray(variantWrapper.media)) {
      variantWrapper.media.forEach((m: any) => {
        if (m?.download) {
          delete m.download;
        }
      });
    }
  }

  private cleanFluidLessonSections(sections: any[]): void {
    sections.forEach((section: any) => {
      if (section.variants) {
        section.variants.forEach((variantWrapper: any) => {
          this.cleanFluidLessonVariant(variantWrapper);
        });
      }
    });
  }

  async deleteFluidLessonPlanMedia(fluidLpId: number) {
    this.transaction('rw', this.fluidLessonPlanDetail, async () => {
      const fluidLessonDetail = await this.fluidLessonPlanDetail.get(fluidLpId);
      if (!fluidLessonDetail) {
        return;
      }

      delete fluidLessonDetail.downLoadStatus;
      delete fluidLessonDetail.downloadProgress;
      delete fluidLessonDetail.downloadErrorCode;

      if (fluidLessonDetail.sections) {
        this.cleanFluidLessonSections(fluidLessonDetail.sections);
      }

      await this.fluidLessonPlanDetail.put(fluidLessonDetail);
      toast('Removed from downloads', {
        toastId: 'fluidLessonDownloadRemove',
      });
    });
  }

  private cleanMediaFromContent(content: any) {
    if (content && typeof content === 'object') {
      if (content.type === 'doc' && Array.isArray(content.content)) {
        content.content.forEach((node: any) => {
          this.cleanMediaFromContent(node);
        });
      } else if (content.type === 'image' && content.attrs) {
        if (content.attrs.download) {
          delete content.attrs.download;
        }
      } else if (Array.isArray(content)) {
        content.forEach((item) => {
          this.cleanMediaFromContent(item);
        });
      } else {
        Object.keys(content).forEach((key) => {
          if (key === 'media' && Array.isArray(content[key])) {
            content[key].forEach((m: any) => {
              if (m?.download) {
                delete m.download;
              }
            });
          } else if (content[key] && typeof content[key] === 'object') {
            this.cleanMediaFromContent(content[key]);
          }
        });
      }
    }
  }

  async retryLpDownload(lpId: number, chapterId: number) {
    try {
      const lessonDetail = await this.lessonPlanDetail.get(lpId);
      const lesson = await this.lessonPlans.get(lpId);
      if (!lessonDetail || !lesson) {
        return;
      }
      const bookInfo = lesson?.bookInfo.filter(
        (info) => info.bookChapter === chapterId,
      )?.[0];
      const chapterDetails = await this.chapters.get(chapterId);
      analyticsService.trackEvent(EventNames.lessonRetryDownload, {
        epGradesubjectId: bookInfo.gradeSubject,
        epChapterName: chapterDetails?.title ?? '',
        epLpId: lesson.id,
      });

      lessonDetail.downLoadStatus = DownloadStatus.Downloading;
      lessonDetail.downLoadProgress = 0;
      lessonDetail.downloadErrorCode = DownloadStatus.None;
      await this.lessonPlanDetail.put(lessonDetail);
      const updatedLp = await this.lessonPlanDetail.get(lesson.id);
      if (updatedLp) {
        await this.retryDownloadLessonPlan(updatedLp, chapterId);
      }
    } catch (e) {}
  }

  async retryFluidLpDownload(lpId: number, chapterId: number) {
    try {
      const fluidLessonDetail = await this.getFluidLessonPlanDetailRecord(lpId);
      const lesson = await this.lessonPlans.get(lpId);
      if (!fluidLessonDetail || !lesson) {
        return;
      }
      const bookInfo = lesson?.bookInfo.filter(
        (info) => info.bookChapter === chapterId,
      )?.[0];
      const chapterDetails = await this.chapters.get(chapterId);
      analyticsService.trackEvent(EventNames.lessonRetryDownload, {
        epGradesubjectId: bookInfo.gradeSubject,
        epChapterName: chapterDetails?.title ?? '',
        epLpId: lesson.id,
        epFluidLpId: fluidLessonDetail.id,
      });

      fluidLessonDetail.downLoadStatus = DownloadStatus.Downloading;
      fluidLessonDetail.downloadProgress = 0;
      fluidLessonDetail.downloadErrorCode = DownloadStatus.None;
      await this.fluidLessonPlanDetail.put(fluidLessonDetail);

      const updatedFluidLp = await this.getFluidLessonPlanDetailRecord(lpId);
      if (updatedFluidLp) {
        await this.retryFluidDownloadLessonPlan(updatedFluidLp, chapterId);
      }
    } catch (e) {}
  }

  async retryFluidDownloadLessonPlan(
    fluidLessonPlanDetail: FluidLessonPlanDetailRecord,
    chapterId: number,
  ) {
    const mediaObject = this.getFluidLessonPlanDownloadMediaObject(
      fluidLessonPlanDetail,
    );
    const mediaAssetArray = Object.values(mediaObject);
    mediaAssetArray.forEach((data) => {
      data.modelDownloadAddedInformation = {
        chapterId: chapterId,
      };
    });
    if (mediaAssetArray.length) {
      downloadManagerService.downloadMediaAsset(
        Object.values(mediaObject),
        true,
      );
    }
  }
  async setDownloading(
    lessonDetail: lessonPlanDetailRecord,
    totalAssets: number,
    chapterId: number,
  ) {
    const lesson = await this.lessonPlans.get(lessonDetail.id);
    const bookInfo = lesson?.bookInfo.filter(
      (info) => info.bookChapter === chapterId,
    )?.[0];
    const chapterInformation = await this.chapters.get(chapterId);

    const context: LessonPlanDownloadContext = {
      lessonId: lessonDetail.id,
      chapterId,
      gradeSubject: bookInfo?.gradeSubject ?? 0,
      chapterTitle: chapterInformation?.title ?? '',
      isFluid: false,
    };

    await this.setLessonDownloading(context, totalAssets);

    if (totalAssets === 0) {
      this.getLessonPlanDownloadCompleteStatus(
        lessonDetail,
        lesson?.bookInfo[0].gradeSubject ?? 0,
        chapterInformation?.title ?? '',
      );
    }
  }

  async setFluidLessonPlanDownloading(
    fluidLessonDetail: FluidLessonPlanDetailRecord,
    totalAssets: number,
    chapterId: number,
  ) {
    const lesson = await this.lessonPlans.get(fluidLessonDetail.legacyLp!);
    const bookInfo = lesson?.bookInfo.filter(
      (info) => info.bookChapter === chapterId,
    )?.[0];
    const chapterInformation = await this.chapters.get(chapterId);

    const context: LessonPlanDownloadContext = {
      lessonId: fluidLessonDetail.legacyLp!,
      chapterId,
      gradeSubject: bookInfo?.gradeSubject ?? 0,
      chapterTitle: chapterInformation?.title ?? '',
      isFluid: true,
      fluidLpId: fluidLessonDetail.id,
    };

    await this.setLessonDownloading(context, totalAssets);
  }

  private getFluidLessonPlanDownloadCompleteStatus(
    fluidLessonDetail: FluidLessonPlanDetailRecord,
    gradeSubjectId: number,
    lpChapterName: string,
  ) {
    analyticsService.trackEvent(EventNames.lessonDownloadComplete, {
      epGradesubjectId: gradeSubjectId,
      epChapterName: lpChapterName,
      epLpId: fluidLessonDetail.legacyLp ?? 0,
      epFluidLpId: fluidLessonDetail.id,
    });
  }

  async getFluidLessonPlanDetailRecord(
    lessonPlanId: number,
  ): Promise<FluidLessonPlanDetailRecord | undefined> {
    return this.fluidLessonPlanDetail
      .where('legacyLp')
      .equals(lessonPlanId)
      .first();
  }

  private updateTipTapContentWithDownloads(
    content: any,
    downloadMap: Map<string, DownloadInfo>,
  ) {
    if (content && typeof content === 'object') {
      if (content.type === 'image' && content.attrs?.src) {
        const download = downloadMap.get(content.attrs.src);
        if (download) {
          content.attrs.download = download;
        }
      } else if (Array.isArray(content.content)) {
        content.content.forEach((node: any) => {
          this.updateTipTapContentWithDownloads(node, downloadMap);
        });
      } else if (typeof content === 'object') {
        Object.keys(content).forEach((key) => {
          if (content[key] && typeof content[key] === 'object') {
            this.updateTipTapContentWithDownloads(content[key], downloadMap);
          }
        });
      }
    }
  }

  private buildMediaLookupMap(
    fluidLp: FluidLessonPlanDetailRecord,
  ): Map<string, DownloadInfo> {
    const mediaLookup = new Map<string, DownloadInfo>();

    if (!fluidLp.sections) return mediaLookup;

    fluidLp.sections.forEach((section: any) => {
      if (section.variants) {
        section.variants.forEach((variantWrapper: any) => {
          if (variantWrapper.media && Array.isArray(variantWrapper.media)) {
            variantWrapper.media.forEach((media: any) => {
              if (media.download && media.link) {
                mediaLookup.set(media.link, media.download);
              }
            });
          }
        });
      }
    });

    return mediaLookup;
  }

  private updateVariantContent(
    variantWrapper: any,
    mediaLookup: Map<string, DownloadInfo>,
  ) {
    if (variantWrapper.variant?.content) {
      this.updateTipTapContentWithDownloads(
        variantWrapper.variant.content,
        mediaLookup,
      );
    }
  }

  private syncMediaArraysToTipTapContent(fluidLp: FluidLessonPlanDetailRecord) {
    if (!fluidLp.sections) return;

    const mediaLookup = this.buildMediaLookupMap(fluidLp);

    fluidLp.sections.forEach((section: any) => {
      if (section.variants) {
        section.variants.forEach((variantWrapper: any) => {
          this.updateVariantContent(variantWrapper, mediaLookup);
        });
      }
    });
  }

  private checkVariantNeedsSync(variantWrapper: any): boolean {
    if (!variantWrapper.media || !Array.isArray(variantWrapper.media)) {
      return false;
    }
    return variantWrapper.media.some((media: any) => media.download);
  }

  private checkSectionNeedsSync(section: any): boolean {
    if (!section.variants) {
      return false;
    }

    for (const variantWrapper of section.variants as any[]) {
      if (this.checkVariantNeedsSync(variantWrapper)) {
        return true;
      }
    }
    return false;
  }

  private checkFluidLessonNeedsSync(
    fluidLp: FluidLessonPlanDetailRecord,
  ): boolean {
    if (!fluidLp.sections) {
      return false;
    }

    for (const section of fluidLp.sections) {
      if (this.checkSectionNeedsSync(section)) {
        return true;
      }
    }
    return false;
  }

  async ensureFluidLessonPlanMediaSync(
    fluidLessonPlans: FluidLessonPlanDetailRecord[],
  ): Promise<void> {
    const updatedPlans: FluidLessonPlanDetailRecord[] = [];

    for (const fluidLp of fluidLessonPlans) {
      if (this.checkFluidLessonNeedsSync(fluidLp)) {
        this.syncMediaArraysToTipTapContent(fluidLp);
        updatedPlans.push(fluidLp);
      }
    }

    if (updatedPlans.length > 0) {
      await this.fluidLessonPlanDetail.bulkPut(updatedPlans);
    }
  }

  private processVariantWrapperForDownloads(
    variantWrapper: any,
    downloadMap: Map<string, DownloadInfo>,
  ): void {
    if (variantWrapper.variant?.content) {
      this.updateTipTapContentWithDownloads(
        variantWrapper.variant.content,
        downloadMap,
      );
    }

    if (variantWrapper.media && Array.isArray(variantWrapper.media)) {
      variantWrapper.media = variantWrapper.media.map((media: any) => {
        const download = downloadMap.get(media.link);
        return download ? { ...media, download } : media;
      });
    }
  }

  private processSectionForDownloads(
    section: any,
    downloadMap: Map<string, DownloadInfo>,
  ): void {
    if (section.variants) {
      section.variants.forEach((variantWrapper: any) => {
        this.processVariantWrapperForDownloads(variantWrapper, downloadMap);
      });
    }
  }

  private processFluidLessonDetailForDownloads(
    fluidLessonDetail: any,
    downloadMap: Map<string, DownloadInfo>,
  ): void {
    if (fluidLessonDetail.sections) {
      fluidLessonDetail.sections.forEach((section: any) => {
        this.processSectionForDownloads(section, downloadMap);
      });
    }
  }

  async updateLessonPlanDetailsWithDownloads(downloads: DownloadInfo[]) {
    // MC20-18113: read each detail table at most ONCE per call, and skip
    // `Completed` rows at the Dexie filter level so 50KB-1MB of TipTap/step
    // JSON per row never materialises into V8 for already-downloaded LPs.
    // Strip the phantom `lpPdfMedia` field at the read boundary while we're
    // at it (write-only field, no reader anywhere in apps/ or libs/).
    const lpDetails = await this.lessonPlanDetail
      .filter((r) => r.downLoadStatus !== DownloadStatus.Completed)
      .toArray();
    const fluidLpDetails = await this.fluidLessonPlanDetail
      .filter((r) => r.downLoadStatus !== DownloadStatus.Completed)
      .toArray();

    const lessonPlanIds = new Set<number>();
    const lpDownloads: DownloadInfo[] = [];
    const downloadMap = new Map<string, DownloadInfo>();
    const updatedLessonPlans: any[] = [];
    const updatedFluidLessonPlans: any[] = [];
    const downloadRequests: DownloadRequest[] = [];

    for (const download of downloads) {
      if (download.tag.modelDownloadName === ModelDownloadName.LessonDetail) {
        lpDownloads.push(download);
        downloadMap.set(download.url, download);
        const lpId = download.tag.modelDownloadRequestId;

        const lessonDetail = lpDetails.find((lp) => lp.id === lpId);
        const fluidLessonDetail = fluidLpDetails.find(
          (fluidLp) => fluidLp.legacyLp === lpId,
        );

        if (!lessonDetail && !fluidLessonDetail) {
          lessonPlanIds.add(lpId);
        }
      }
    }

    if (lessonPlanIds.size && (await Network.getStatus()).connected) {
      await fetchSelectedLessonPlan([...lessonPlanIds], null);
      await fetchSelectedFluidLessonPlan([...lessonPlanIds], null);
    }

    lpDetails.forEach((lpDetail) => {
      // Strip phantom field at the read boundary.
      const { lpPdfMedia: _drop, ...rest } = lpDetail as any;
      const lessonDetail = rest as lessonPlanDetailRecord;

      for (const key in lessonDetail) {
        const details = lessonDetail[key];
        if (Array.isArray(details)) {
          details.forEach((detail: any) => {
            if (detail?.media?.length) {
              detail.media = detail.media.map((media: any) => {
                const download = downloadMap.get(media.link);
                return download ? { ...media, download } : media;
              });
            }
            if (detail?.textBody?.length) {
              detail.textBody = detail.textBody.map((textItem: any) => {
                if (textItem?.media?.length) {
                  textItem.media = textItem.media.map((media: any) => {
                    const download = downloadMap.get(media.link);
                    return download ? { ...media, download } : media;
                  });
                }
                return textItem;
              });
            }
          });
        }
      }
      // Filter already excluded Completed rows; this guard is defence-in-depth.
      if (lessonDetail.downLoadStatus !== DownloadStatus.Completed) {
        updatedLessonPlans.push(lessonDetail);
      }
    });

    fluidLpDetails.forEach((fluidLpDetail) => {
      const fluidLessonDetail = { ...fluidLpDetail };

      this.processFluidLessonDetailForDownloads(fluidLessonDetail, downloadMap);

      if (fluidLessonDetail.downLoadStatus !== DownloadStatus.Completed) {
        updatedFluidLessonPlans.push(fluidLessonDetail);
      }
    });

    try {
      await this.updateLessonsAlreadyDownloadedStatus(updatedLessonPlans);
      await this.updateFluidLessonsAlreadyDownloadedStatus(
        updatedFluidLessonPlans,
      );
      if (downloadRequests.length) {
        downloadRequestModel.addBulkDownloadRequest(downloadRequests);
      }
    } catch (e) {}
  }

  private updateFluidLessonMediaWithDownload(
    fluidLessonPlanDetail: FluidLessonPlanDetailRecord,
    downloadInfo: DownloadInfo,
  ): void {
    if (!fluidLessonPlanDetail.sections) return;

    fluidLessonPlanDetail.sections.forEach((section: any) => {
      if (section.variants) {
        section.variants.forEach((variantWrapper: any) => {
          if (variantWrapper.media && Array.isArray(variantWrapper.media)) {
            variantWrapper.media = variantWrapper.media.map((media: any) => {
              if (media.link === downloadInfo.url) {
                return { ...media, download: downloadInfo };
              }
              return media;
            });
          }
        });
      }
    });
  }

  private calculateFluidLessonDownloadStatus(
    mediaAssetArray: any[],
    failed: boolean,
    currentStatus?: DownloadStatus,
  ): DownloadStatus {
    const downloadProgress = this.calculateAggregateProgress(mediaAssetArray);

    if (failed) {
      return DownloadStatus.Failed;
    }

    if (downloadProgress === 100) {
      return DownloadStatus.Completed;
    } else if (downloadProgress > 0) {
      return DownloadStatus.Downloading;
    }
    return DownloadStatus.None;
  }

  private handleFluidLessonDownloadStatusChange(
    fluidLessonPlanDetail: FluidLessonPlanDetailRecord,
    downloadStatus: DownloadStatus,
    gradeSubject: number,
    chapterTitle: string,
  ): void {
    if (
      fluidLessonPlanDetail.downLoadStatus !== DownloadStatus.Completed &&
      downloadStatus === DownloadStatus.Completed
    ) {
      this.getFluidLessonPlanDownloadCompleteStatus(
        fluidLessonPlanDetail,
        gradeSubject,
        chapterTitle,
      );
    }

    if (
      downloadStatus === DownloadStatus.Failed &&
      fluidLessonPlanDetail.downLoadStatus !== DownloadStatus.Failed
    ) {
      this.getFluidLessonPlanDownloadErrorStatus(
        fluidLessonPlanDetail,
        gradeSubject,
      );
    }
  }

  async saveDownloadedFluidLpOnEvent(
    downloadInfo: DownloadInfo,
    fluidLessonPlanDetail: FluidLessonPlanDetailRecord,
    gradeSubject: number,
    chapterTitle: string,
  ) {
    if (!fluidLessonPlanDetail) return null;
    let downloadStatus;
    let downloadProgress;
    let failed = false;
    let errorCode;
    try {
      if (downloadInfo.status === DownloadStatus.Failed) {
        failed = true;
        errorCode = downloadInfo.error ?? DownloadStatus.Failed;
      } else if (downloadInfo.status === DownloadStatus.Paused) {
        downloadStatus = DownloadStatus.Downloading;
      }
      this.updateFluidLessonMediaWithDownload(
        fluidLessonPlanDetail,
        downloadInfo,
      );
      this.syncMediaArraysToTipTapContent(fluidLessonPlanDetail);
      await this.fluidLessonPlanDetail.put(fluidLessonPlanDetail);

      const updatedLessonPlan = await this.fluidLessonPlanDetail.get(
        fluidLessonPlanDetail.id,
      );
      if (!updatedLessonPlan) return;

      const mediaObject =
        this.getFluidLessonPlanDownloadMediaObject(updatedLessonPlan);
      const mediaAssetArray = Object.values(mediaObject);
      if (!failed) {
        for (const key in mediaObject) {
          const asset = mediaObject[key];
          if (asset.download?.status === DownloadStatus.Failed) {
            failed = true;
            const mediaStatus = getMediaStatus(asset);
            errorCode =
              (mediaStatus?.errorCode as DownloadStatus) ??
              DownloadStatus.Failed;
            break;
          }
        }
      }
      downloadProgress = this.calculateAggregateProgress(mediaAssetArray);
      if (!failed) {
        if (downloadProgress === 100) {
          downloadStatus = DownloadStatus.Completed;
        } else if (
          downloadProgress > 0 &&
          downloadInfo.status !== DownloadStatus.Paused
        ) {
          downloadStatus = DownloadStatus.Downloading;
        }
      } else {
        downloadStatus = DownloadStatus.Failed;
      }
      if (
        updatedLessonPlan.downLoadStatus !== DownloadStatus.Completed &&
        downloadStatus === DownloadStatus.Completed
      ) {
        this.getFluidLessonPlanDownloadCompleteStatus(
          fluidLessonPlanDetail,
          gradeSubject,
          chapterTitle,
        );
      }
      if (
        downloadStatus === DownloadStatus.Failed &&
        updatedLessonPlan.downLoadStatus !== DownloadStatus.Failed
      ) {
        this.getFluidLessonPlanDownloadErrorStatus(
          fluidLessonPlanDetail,
          gradeSubject,
        );
      }

      const needsUpdate =
        updatedLessonPlan.downLoadStatus !== downloadStatus ||
        updatedLessonPlan.downloadProgress !== downloadProgress ||
        updatedLessonPlan.downloadErrorCode !== errorCode;

      if (needsUpdate) {
        updatedLessonPlan.downLoadStatus = failed
          ? DownloadStatus.Failed
          : downloadStatus;
        updatedLessonPlan.downloadErrorCode = failed
          ? errorCode
          : DownloadStatus.None;
        updatedLessonPlan.downloadProgress = downloadProgress;
        await this.fluidLessonPlanDetail.put(updatedLessonPlan);
      }
    } catch (error) {}
  }

  private getFluidLessonPlanDownloadErrorStatus(
    fluidLessonPlanDetail: FluidLessonPlanDetailRecord,
    gradeSubjectId: number,
  ) {
    analyticsService.trackEvent(EventNames.lessonDownloadError, {
      epLpId: fluidLessonPlanDetail.legacyLp ?? 0,
      epFluidLpId: fluidLessonPlanDetail.id,
      epGradesubjectId: gradeSubjectId,
      epLpDownloadErrorMessage: fluidLessonPlanDetail?.downloadErrorCode ?? '',
    });
  }

  async saveDownloadedLpOnEvent(
    downloadInfo: DownloadInfo,
    lessonPlanDetail: lessonPlanDetailRecord,
    gradeSubject: number,
    chapterTitle: string,
  ) {
    if (!lessonPlanDetail) return null;
    let downloadStatus;
    let downloadProgress;
    const failed = false;
    let errorCode;
    try {
      for (const key in lessonPlanDetail) {
        if (Array.isArray(lessonPlanDetail[key])) {
          for (const detail of lessonPlanDetail[key]) {
            if (typeof detail === 'object') {
              if (detail.media?.length) {
                detail.media = detail.media.map((media: any) => {
                  if (media.link === downloadInfo.url) {
                    return { ...media, download: downloadInfo };
                  }
                  return media;
                });
              }
              if (detail.textBody?.length) {
                detail.textBody = detail.textBody.map((textItem: any) => {
                  if (textItem.media?.length) {
                    textItem.media = textItem.media.map((media: any) => {
                      if (media.link === downloadInfo.url) {
                        return { ...media, download: downloadInfo };
                      }
                      return media;
                    });
                  }
                  return textItem;
                });
              }
            }
          }
        }
      }

      const mediaObject = lessonPlanDB.getDownloadMediaObject(lessonPlanDetail);
      const mediaAssetArray = Object.values(mediaObject);
      downloadProgress = this.calculateAggregateProgress(mediaAssetArray);

      // Update status based on download progress
      if (downloadProgress === 100) {
        downloadStatus = DownloadStatus.Completed;
      } else if (downloadProgress > 0 && !failed) {
        downloadStatus = DownloadStatus.Downloading;
      }
      if (
        lessonPlanDetail.downLoadStatus !== DownloadStatus.Completed &&
        downloadStatus === DownloadStatus.Completed
      ) {
        this.getLessonPlanDownloadCompleteStatus(
          lessonPlanDetail,
          gradeSubject,
          chapterTitle,
        );
      }
      if (
        downloadStatus === DownloadStatus.Failed &&
        lessonPlanDetail.downLoadStatus !== DownloadStatus.Failed
      ) {
        this.getLessonPlanDownloadErrorStatus(lessonPlanDetail, gradeSubject);
      }
      if (
        lessonPlanDetail.downLoadStatus !== downloadStatus ||
        lessonPlanDetail.downloadProgress !== downloadProgress ||
        lessonPlanDetail.downloadErrorCode !== errorCode
      ) {
        lessonPlanDetail.downLoadStatus = failed
          ? DownloadStatus.Failed
          : downloadStatus;
        lessonPlanDetail.downloadErrorCode = failed
          ? errorCode
          : DownloadStatus.None;
        lessonPlanDetail.downloadProgress = downloadProgress;
      }

      this.lessonPlanDetail.put(lessonPlanDetail);
    } catch (error) {}
  }

  getDownloadData() {
    downloadManagerService.onDownload((downloadInfo) => {
      if (
        downloadInfo.tag.modelDownloadName === ModelDownloadName.LessonDetail
      ) {
        this.downloadLpEventHandler(downloadInfo);
      }
    });
  }
  downloadLpEventHandler(downloadInfo: DownloadInfo) {
    if (
      downloadInfo.status === DownloadStatus.Completed ||
      downloadInfo.status === DownloadStatus.Failed ||
      downloadInfo.status === DownloadStatus.Downloading ||
      downloadInfo.status === DownloadStatus.Paused
    ) {
      this.updateLessonPlanDetailAssets(downloadInfo);
      this.updateFluidLessonPlanDetailAssets(downloadInfo);
    }
  }

  async updateFluidLessonPlanDetailAssets(downloadInfo: DownloadInfo) {
    try {
      const lesson = await this.lessonPlans.get(
        downloadInfo.tag.modelDownloadRequestId,
      );
      if (!lesson) {
        return;
      }

      const chapter =
        downloadInfo.tag?.modelDownloadAddedInformation?.['chapterId'] ?? null;
      const bookInfo = lesson.bookInfo.find(
        (info) => info.bookChapter.toString() === chapter?.toString(),
      );

      const chapterInformation = await this.chapters.get(chapter);
      if (!chapterInformation || !chapter || !bookInfo) {
        return;
      }

      this.transaction('rw', this.fluidLessonPlanDetail, async () => {
        const fluidLessonPlanDetail = await this.fluidLessonPlanDetail
          .where('legacyLp')
          .equals(downloadInfo.tag.modelDownloadRequestId)
          .first();

        if (fluidLessonPlanDetail) {
          this.saveDownloadedFluidLpOnEvent(
            downloadInfo,
            fluidLessonPlanDetail,
            bookInfo?.gradeSubject,
            chapterInformation.title,
          );
        }
      });
    } catch (e) {}
  }

  async updateLessonPlanDetailAssets(downloadInfo: DownloadInfo) {
    try {
      const lesson = await this.lessonPlans.get(
        downloadInfo.tag.modelDownloadRequestId,
      );
      if (!lesson) {
        return;
      }
      const chapter =
        downloadInfo.tag?.modelDownloadAddedInformation?.['chapterId'] ?? null;
      const bookInfo = lesson.bookInfo.find(
        (info) => info.bookChapter.toString() === chapter?.toString(),
      );

      const chapterInformation = await this.chapters.get(chapter);
      if (!chapterInformation || !chapter || !bookInfo) {
        return;
      }

      this.transaction('rw', this.lessonPlanDetail, async () => {
        const lessonPlanDetail = await this.lessonPlanDetail.get(
          downloadInfo.tag.modelDownloadRequestId,
        );
        if (lessonPlanDetail) {
          this.saveDownloadedLpOnEvent(
            downloadInfo,
            lessonPlanDetail,
            bookInfo?.gradeSubject,
            chapterInformation.title,
          );
        }
      });
    } catch (e) {}
  }

  // update for lp download test with event
  async updateLessonPlanDetailAssetsEvents(
    downloadInfo: DownloadInfo,
    lessonPlanDetail: lessonPlanDetailRecord,
  ): Promise<lessonPlanDetailRecord | null> {
    if (!lessonPlanDetail) return null;

    let downloadStatus;
    let downloadProgress;
    let failed = false;
    let errorCode;

    try {
      // Iterate through lessonPlanDetail and process the media assets
      for (const key in lessonPlanDetail) {
        if (Array.isArray(lessonPlanDetail[key])) {
          for (const detail of lessonPlanDetail[key]) {
            if (typeof detail === 'object' && detail.media?.length) {
              for (const media of detail.media) {
                // Process the media if it matches the download URL
                if (media.link === downloadInfo.url) {
                  media.download = downloadInfo;
                  const mediaStatus = getMediaStatus(media)?.status;

                  // Check for failed status
                  if (mediaStatus === DownloadStatus.Failed) {
                    failed = true;
                    errorCode = media.download.error;
                    if (
                      lessonPlanDetail?.downLoadStatus !==
                        DownloadStatus.Failed &&
                      failed
                    ) {
                      downloadStatus = DownloadStatus.Failed;
                      lessonPlanDetail.downLoadStatus = DownloadStatus.Failed;
                    }
                  }
                }
              }
            }
          }
        }
      }

      // Get the media objects and calculate the aggregated progress
      const mediaObject = lessonPlanDB.getDownloadMediaObject(lessonPlanDetail);
      const mediaAssetArray = Object.values(mediaObject);
      downloadProgress = this.calculateAggregateProgress(mediaAssetArray);

      // Update status based on download progress
      if (downloadProgress === 100) {
        downloadStatus = DownloadStatus.Completed;
      } else if (downloadProgress > 0 && !failed) {
        downloadStatus = DownloadStatus.Downloading;
      }

      // Only update the lessonPlanDetail if there are changes
      if (
        lessonPlanDetail.downLoadStatus !== downloadStatus ||
        lessonPlanDetail.downloadProgress !== downloadProgress ||
        lessonPlanDetail.downloadErrorCode !== errorCode
      ) {
        lessonPlanDetail.downLoadStatus = failed
          ? DownloadStatus.Failed
          : downloadStatus;
        lessonPlanDetail.downloadErrorCode = failed
          ? errorCode
          : DownloadStatus.None;
        lessonPlanDetail.downloadProgress = downloadProgress;
      }

      return lessonPlanDetail;
    } catch (error) {
      return null;
    }
  }

  calculateAggregateProgress(items: DownloadRequest[]) {
    const assetsDownloadProgresses: number[] = [];
    let count = 0;
    items.forEach((item: any) => {
      count++;
      if (
        item.download &&
        item.download.downloaded &&
        item.download.total &&
        item.download.total > 0
      ) {
        assetsDownloadProgresses.push(
          (item?.download.downloaded / item?.download.total) * 100,
        );
      }
    });

    // Avoid division by zero
    if (items.length === 0) {
      return 0;
    }
    const progress = Math.floor(
      assetsDownloadProgresses.reduce((a, b) => a + b, 0) / items.length,
    );
    return progress;
  }

  private calculateLessonDownloadStatus(
    mediaObject: { [key: string]: DownloadRequest },
    currentStatus?: string,
  ): LessonPlanUpdateResult {
    const mediaLength = Object.keys(mediaObject).length;

    if (mediaLength === 0) {
      return {
        downloadStatus: DownloadStatus.Completed,
        downloadProgress: 100,
        downloadErrorCode: DownloadStatus.None,
      };
    }

    let downloadStatus: DownloadStatus;
    let failed = false;
    let errorCode: DownloadStatus = DownloadStatus.None;

    for (const key in mediaObject) {
      const asset = mediaObject[key];
      if (asset.download?.status === DownloadStatus.Failed) {
        failed = true;
        const mediaStatus = getMediaStatus(asset);
        errorCode =
          (mediaStatus?.errorCode as DownloadStatus) ?? DownloadStatus.Failed;
        downloadStatus = DownloadStatus.Failed;
        break;
      }
    }

    const mediaAssetArray = Object.values(mediaObject).filter(
      (download) => download.download !== undefined,
    );

    const downloadProgress = this.calculateAggregateProgress(mediaAssetArray);

    if (!failed) {
      if (downloadProgress === 100) {
        downloadStatus = DownloadStatus.Completed;
      } else if (downloadProgress > 0 && currentStatus !== undefined) {
        downloadStatus = DownloadStatus.Downloading;
      } else {
        downloadStatus = DownloadStatus.None;
      }
    }

    return {
      downloadStatus: downloadStatus!,
      downloadProgress,
      downloadErrorCode: errorCode,
    };
  }

  private trackLessonDownloadEvent(
    eventName: EventNames,
    context: LessonPlanDownloadContext,
    additionalProps?: Record<string, any>,
  ) {
    const baseProps = {
      epGradesubjectId: context.gradeSubject,
      epChapterName: context.chapterTitle,
      epLpId: context.lessonId,
      ...(context.isFluid && { epFluidLpId: context.fluidLpId }),
      ...additionalProps,
    };

    analyticsService.trackEvent(eventName, baseProps);
  }

  private async setLessonDownloading(
    context: LessonPlanDownloadContext,
    totalAssets: number,
  ) {
    this.trackLessonDownloadEvent(EventNames.lessonDownload, context);

    const downloadStatus =
      totalAssets > 0 ? DownloadStatus.Downloading : DownloadStatus.Completed;
    const downloadProgress = totalAssets > 0 ? 0 : 100;

    if (context.isFluid) {
      const fluidLessonDetail = await this.fluidLessonPlanDetail.get(
        context.fluidLpId!,
      );
      if (fluidLessonDetail) {
        fluidLessonDetail.downLoadStatus = downloadStatus;
        fluidLessonDetail.downloadProgress = downloadProgress;
        fluidLessonDetail.downloadErrorCode = DownloadStatus.None;
        await this.fluidLessonPlanDetail.put(fluidLessonDetail);

        if (downloadStatus === DownloadStatus.Completed) {
          this.trackLessonDownloadEvent(
            EventNames.lessonDownloadComplete,
            context,
          );
        }
      }
    } else {
      const lessonDetail = await this.lessonPlanDetail.get(context.lessonId);
      if (lessonDetail) {
        lessonDetail.downLoadStatus = downloadStatus;
        lessonDetail.downloadProgress = downloadProgress;
        lessonDetail.downloadErrorCode = DownloadStatus.None;
        await this.lessonPlanDetail.put(lessonDetail);

        if (downloadStatus === DownloadStatus.Completed) {
          this.trackLessonDownloadEvent(
            EventNames.lessonDownloadComplete,
            context,
          );
        }
      }
    }
  }

  private async updateLessonDownloadStatus<
    T extends lessonPlanDetailRecord | FluidLessonPlanDetailRecord,
  >(lessons: T[], isFluid = false) {
    try {
      const updatedLessons: T[] = [];

      for (const lesson of lessons) {
        const mediaObject = isFluid
          ? this.getFluidLessonPlanDownloadMediaObject(
              lesson as FluidLessonPlanDetailRecord,
              false,
            )
          : this.getDownloadMediaObject(
              lesson as lessonPlanDetailRecord,
              false,
            );

        const result = this.calculateLessonDownloadStatus(
          mediaObject,
          lesson.downLoadStatus as string,
        );

        if (
          lesson.downLoadStatus !== result.downloadStatus ||
          lesson.downloadProgress !== result.downloadProgress ||
          lesson.downloadErrorCode !== result.downloadErrorCode
        ) {
          lesson.downLoadStatus = result.downloadStatus;
          lesson.downloadProgress = result.downloadProgress;
          lesson.downloadErrorCode = result.downloadErrorCode;
          updatedLessons.push(lesson);
        }
      }

      if (updatedLessons.length) {
        if (isFluid) {
          await this.fluidLessonPlanDetail.bulkPut(
            updatedLessons as FluidLessonPlanDetailRecord[],
          );
        } else {
          await this.lessonPlanDetail.bulkPut(
            updatedLessons as lessonPlanDetailRecord[],
          );
        }
      }
    } catch (e) {}
  }

  processStatements(
    statements: ILessonPlanStep[] | StepData[],
  ): ILessonPlanStep[] | StepData[] {
    const uniqueLinks = new Set<string>();

    if (statements.length > 0 && 'textBody' in statements[0]) {
      const processedStepData = (statements as StepData[]).map(
        (stepData: StepData) => {
          if (stepData.textBody && stepData.textBody.length > 0) {
            stepData.textBody = stepData.textBody.map((textItem: TextBody) => {
              if (textItem.media && textItem.media.length > 0) {
                textItem.media = textItem.media.filter((media: IMedia) => {
                  if (!uniqueLinks.has(media.link)) {
                    uniqueLinks.add(media.link);
                    return true;
                  }
                  return false;
                });
              }
              return textItem;
            });
          }
          return stepData;
        },
      );
      return processedStepData;
    } else {
      const processedStatements = (statements as ILessonPlanStep[]).map(
        (statement: ILessonPlanStep, index: number) => {
          const isAdjacent = index > 0 && statements[index - 1] === statement;
          if (statement.media && statement.media.length > 0 && !isAdjacent) {
            statement.media = statement.media.filter((media: IMedia) => {
              if (!uniqueLinks.has(media.link)) {
                uniqueLinks.add(media.link);
                return true;
              }
              return false;
            });
          }
          return statement;
        },
      );
      return processedStatements;
    }
  }
  private async getLessonPlanSyncData(
    model: Table,
  ): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await model
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Created, SyncRecordStatus.Updated])
      .toArray();

    return records.map((record) => {
      if (record.syncStatus === SyncRecordStatus.Updated) {
        return { ...record, syncStatus: SyncRecordStatus.Created };
      }
      return record;
    });
  }

  private async getLessonPlanSyncDataTimetable(): Promise<
    PromiseExtended<SyncRecord[]>
  > {
    const records = await this.schoolClassTimetables
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Updated, SyncRecordStatus.Deleted])
      .toArray();

    return records;
  }

  getNewCreatedTimetableRecord(): PromiseExtended<SyncRecord[]> {
    return this.schoolClassTimetables
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.schoolClassSubject === undefined,
      )
      .toArray();
  }
  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      lessonPlanStatus: await this.getLessonPlanSyncData(this.lessonPlanStatus),
      schoolClassTimetables: [
        ...(await this.getNewCreatedTimetableRecord()),
        ...(await this.getLessonPlanSyncDataTimetable()),
      ],
    };
    return records;
  }
  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;

    const idsToMarkSynced = {
      lessonPlanStatus: syncChanges.lessonPlanStatus?.created.map(
        (record: { id: number }) => record.id,
      ),
      schoolClassTimetables: [
        ...((syncChanges.schoolClassTimetables?.created as any[]) ?? []),
        ...((syncChanges.schoolClassTimetables?.deleted as any[]) ?? []),
      ].map((record: { id: number }) => record.id),
    };
    for (const [model, ids] of Object.entries(idsToMarkSynced)) {
      updates += await this._updateSyncData(
        // @ts-ignore
        this[model],
        ids,
        status,
      );
    }

    // Bug B fix: only hard-delete timetable class entries on success, not on failure
    if (status === SyncRecordStatus.Synced) {
      await this.deleteTimetableClassEntries();
    }

    // When a delete push fails due to CLASS_HAS_OBSERVATIONS, revert those
    // rows from Deleted → Synced so the class reappears in the UI
    if (status === SyncRecordStatus.Failed) {
      const classHasObsIds = (syncChanges.schoolClassTimetables?.deleted ?? [])
        .filter((r: any) => r.failure_reason === 'CLASS_HAS_OBSERVATIONS')
        .map((r: { id: number }) => r.id);
      if (classHasObsIds.length > 0) {
        await this._updateSyncData(
          this.schoolClassTimetables,
          classHasObsIds,
          SyncRecordStatus.Synced,
        );
        await Promise.all(
          classHasObsIds.map((id: number) =>
            this.schoolClassTimetables.update(id, { deletionBlocked: true }),
          ),
        );
      }
    }

    return 1;
  }

  async updateLessonPlanStatusClass(
    chapterId: number,
    lessonPlanId: number,
    schoolClass: number,
  ): Promise<void> {
    try {
      const user = userCache.getUserFromCache();
      const teacherId = user?.profiles.filter(
        (user) => user.type === UserRole.Teacher,
      );
      return this.getLessonPlans(chapterId).then(async (lessonPlans) => {
        lessonPlans.forEach(async (lessonPlan) => {
          if (
            lessonPlan.id === lessonPlanId &&
            lessonPlan.lessonPlanStatusClass
          ) {
            const index = lessonPlan.lessonPlanStatusClass.findIndex(
              (item) => item.schoolClass === schoolClass,
            );

            if (index !== -1) {
              lessonPlan.lessonPlanStatusClass[index] = {
                schoolClass: schoolClass,
                status: new Date().toISOString(),
              };
            } else {
              lessonPlan.lessonPlanStatusClass.push({
                schoolClass: schoolClass,
                status: new Date().toISOString(),
              });
            }
            lessonPlanDB.lessonPlans.update(lessonPlan.id, {
              ...lessonPlan,
              lessonPlanStatusClass: lessonPlan.lessonPlanStatusClass,
            });

            const isExist = await this.lessonPlanStatus
              .where({
                lessonPlan: lessonPlan.id,
                profile: teacherId?.[0].id,
                schoolClass: schoolClass,
              })
              .first();
            if (!isExist) {
              this.lessonPlanStatus.add({
                lessonPlan: lessonPlan.id,
                schoolClass: schoolClass,
                created: new Date().toISOString(),
              } as LessonPlanStatusRecord);
            } else {
              this.lessonPlanStatus
                .where({
                  lessonPlan: lessonPlan.id,
                  profile: teacherId?.[0].id,
                  schoolClass: schoolClass,
                })
                .modify({
                  created: new Date().toISOString(),
                  syncStatus: SyncRecordStatus.Updated,
                });
            }
          }
        });
      });
    } catch {}
  }

  updateChapterData(data: any) {
    lessonPlanDB.chapters.bulkPut(data.chapters.created.data);
    lessonPlanDB.lessonPlans.bulkPut(data.lessonPlans.created.data);
  }
  getGrades(): PromiseExtended<LessonPlanGradeRecord[]> {
    return this.grades
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  getSubjects(): PromiseExtended<LessonPlanSubjectRecord[]> {
    return this.subjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  getCurrentSession() {
    return this.sessions
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .filter((record) => record.isCurrent === true)
      .first();
  }

  async checkAddClassTimetable() {
    const grades = await this.getGrades();
    const subject = await this.getSubjects();
    if (grades.length > 0 && subject.length > 0) {
      return true;
    }
    return false;
  }

  getSubject(subjectId: number): PromiseExtended<LessonPlanSubjectRecord[]> {
    return this.subjects
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.grades.includes(subjectId),
      )

      .sortBy('id');
  }

  addRecordLessonPlanTimetable(timetable: ISchoolClassTimeTable) {
    this.schoolClassTimetables.add({
      shift: timetable.shift,
      daysOfWeek: timetable.daysOfWeek,
      grade: timetable.grade,
      subject: timetable.subject,
      section: timetable.section,
    } as LessonPlanTimetableRecord);
  }

  getDays() {
    return Days;
  }
  getSelectedGrade(gradeId: number) {
    return this.grades
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.id === gradeId,
      )
      .sortBy('id');
  }

  getLessonPlaStatusCount(lessonPlanId: number): PromiseExtended<number> {
    return this.lessonPlanStatus
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.lessonPlan === lessonPlanId,
      )
      .count();
  }
  async removeTimetableEntriesWithSchoolClass() {
    await this.schoolClassTimetables
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.schoolClassSubject === undefined,
      )
      .delete();
  }

  async deleteTimetableClassEntries() {
    await this.schoolClassTimetables
      .filter((record) => record.syncStatus === SyncRecordStatus.Deleted)
      .delete();
  }

  async getTimetableCreatedRecords() {
    return await this.schoolClassTimetables
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record.schoolClassSubject !== undefined,
      )
      .sortBy('id');
  }

  async getTimetableEntries() {
    const isNumeracyEnabled =
      (await userDB.getFeatureFlag(FeatureFlagNames.numeracyEnabled)) ?? false;
    // Do not exclude Deleted records here — they stay visible until the server
    // confirms the deletion and deleteTimetableClassEntries() hard-removes them.
    // This prevents the class from vanishing optimistically before push response.
    const timetableEntries = await this.schoolClassTimetables
      .filter((record) => record.schoolClassSubject !== undefined)
      .sortBy('id');

    const filteredSchoolClassSubjects = await this.schoolClassSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');

    const timetableWithGradeSubject = filteredSchoolClassSubjects.filter(
      (schoolClass) => {
        return timetableEntries.some(
          (timetableEntry) =>
            schoolClass.id === timetableEntry.schoolClassSubject,
        );
      },
    );
    const currentSession = await this.getCurrentSession();
    const schoolClassSections = await this.schoolClasses
      .filter(
        (record) =>
          record.syncStatus !== SyncRecordStatus.Deleted &&
          record?.session == currentSession?.id,
      )
      .sortBy('id');

    const gradeSubject = await this.gradeSubjects
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');

    const timetableWithSectionAndShift = timetableWithGradeSubject
      .map((record) => {
        const timetableEntry = timetableEntries.find(
          (entry) => entry.schoolClassSubject === record.id,
        );

        const schoolClassSection = schoolClassSections.find(
          (data) => data.id === record.schoolClass,
        );

        const gradeSubjectRecord = gradeSubject.find(
          (data) => data.id === record.gradeSubject,
        );

        if (
          timetableEntry &&
          timetableEntry.id != null &&
          schoolClassSection &&
          gradeSubjectRecord
        ) {
          return {
            ...record,
            schoolClassSubjectId: record.id,
            id: timetableEntry.id,
            section: schoolClassSection.section,
            shift: schoolClassSection.shift,
            gradeLabel: gradeSubjectRecord.gradeLabel,
            subjectLabel: gradeSubjectRecord.subjectLabel,
            grade: gradeSubjectRecord.grade,
            subject: gradeSubjectRecord.subject,
            daysOfWeek: timetableEntry.daysOfWeek,
            deletionBlocked: timetableEntry.deletionBlocked ?? false,
          };
        }

        return null;
      })
      .filter((record) => record !== null);

    if (isNumeracyEnabled) {
      return timetableWithSectionAndShift.filter(
        (record) => record?.subjectLabel !== 'Numeracy',
      );
    }

    return timetableWithSectionAndShift;
  }

  async getWeeklyClassCountForUGLPSubjects() {
    const entries = await this.getTimetableEntries();
    const coreLPSubjects = ['english', 'math', 'general science', 'urdu'];
    const filteredEntries = entries.filter(
      (record) =>
        record?.schoolClass &&
        record.daysOfWeek?.length > 0 &&
        coreLPSubjects.includes(record.subjectLabel.toLowerCase()),
    );
    return filteredEntries.reduce((sum, entry) => {
      return sum + (entry.daysOfWeek?.length ?? 0);
    }, 0);
  }

  async getTimeTableClass(gradeSubjectId: number, schoolClass: number) {
    return (await this.getTimetableEntries())
      .filter(
        (record) =>
          record?.gradeSubject === gradeSubjectId &&
          record.schoolClass === schoolClass,
      )
      .filter((record) => record !== null) as ISchoolClassInfo[];
  }

  async getTimeTableClassV2(
    gradeSubjectId: number,
    schoolClass: number,
  ): Promise<ISchoolClassInfo | null> {
    const entries = await this.getTimetableEntries();
    const classDetail = entries.find(
      (record) =>
        record?.gradeSubject === gradeSubjectId &&
        record.schoolClass === schoolClass,
    );

    if (classDetail) {
      return {
        ...classDetail,
        id: classDetail.id ?? 0,
      };
    } else {
      return null;
    }
  }

  getTimetableClassById(
    id: number,
  ): PromiseExtended<LessonPlanTimetableRecord[]> {
    return this.schoolClassTimetables
      .filter(
        (record) =>
          record?.id === id && record?.syncStatus !== SyncRecordStatus.Deleted,
      )
      .toArray();
  }

  /** Returns the count of timetable records that were blocked from deletion
   * (CLASS_HAS_OBSERVATIONS). Used to trigger a toast in the UI. */
  getDeletionBlockedCount(): PromiseExtended<number> {
    return this.schoolClassTimetables
      .filter((record) => record.deletionBlocked === true)
      .count();
  }

  /** Returns grade + section labels for each timetable record blocked from deletion.
   * Used to name the classes in the "cannot delete" toast. */
  async getDeletionBlockedClassLabels(): Promise<string[]> {
    const blocked = await this.schoolClassTimetables
      .filter((record) => record.deletionBlocked === true)
      .toArray();

    const labels: string[] = [];
    for (const record of blocked) {
      const subject = record.schoolClassSubject
        ? await this.schoolClassSubjects.get(record.schoolClassSubject)
        : undefined;
      const schoolClass = subject?.schoolClass
        ? await this.schoolClasses.get(subject.schoolClass)
        : undefined;
      const grade = schoolClass?.grade
        ? await this.grades.get(schoolClass.grade)
        : undefined;
      const section = schoolClass?.section ?? '';
      const gradeLabel = grade?.label ?? `Grade ${record.grade ?? ''}`;
      labels.push(section ? `${gradeLabel} - ${section}` : gradeLabel);
    }
    return labels;
  }

  /** Clears the deletionBlocked flag on all flagged timetable records. */
  async clearDeletionBlockedFlag(): Promise<void> {
    await this.schoolClassTimetables
      .filter((record) => record.deletionBlocked === true)
      .modify({ deletionBlocked: false });
  }

  async getTimeTableClassInformationById(id: number) {
    return (await this.getTimetableEntries()).filter(
      (record) =>
        record?.id === id && record?.syncStatus !== SyncRecordStatus.Deleted,
    );
  }
  async checkTimetableEntryExists(
    grade: number,
    subject: number,
    section: string,
    shift: string,
  ) {
    return (await this.getTimetableEntries()).filter(
      (record: any) =>
        record.syncStatus !== SyncRecordStatus.Deleted &&
        record.grade === grade &&
        record.subject === subject &&
        record.section === section &&
        record.shift === shift.toUpperCase(),
    );
  }

  async getAllLessonPlanDetail(lessonPlans: ILessonPlan[]) {
    return this.lessonPlanDetail
      .where('id')
      .anyOf(lessonPlans.map((lessonPlan) => lessonPlan.id))
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  async getAllFluidLessonPlanDetail(lessonPlans: ILessonPlan[]) {
    return this.fluidLessonPlanDetail
      .where('legacyLp')
      .anyOf(lessonPlans.map((lessonPlan) => lessonPlan.id))
      .filter((record) => record.syncStatus !== SyncRecordStatus.Deleted)
      .sortBy('id');
  }

  async updateSchoolClassTimetable(id: number, dayOfWeek: number[]) {
    await this.schoolClassTimetables.update(id, {
      daysOfWeek: dayOfWeek,
      syncStatus: SyncRecordStatus.Updated,
    });
  }

  async deleteSchoolClassTimetable(id: number) {
    await this.schoolClassTimetables.update(id, {
      syncStatus: SyncRecordStatus.Deleted,
    });
  }

  async getAllSchoolClassesAndGrades() {
    const schoolClasses = await this.getSchoolClasses();
    const grades = await this.getGrades();
    const mappedSchoolClasses = schoolClasses.map((schoolClass) => {
      const grade = grades.find((grade) => grade.id === schoolClass.grade);
      return { ...schoolClass, grade };
    });
    mappedSchoolClasses.sort((a, b) => {
      return (
        (a.grade?.id ?? 0) - (b.grade?.id ?? 0) ||
        (a.shift ?? '').localeCompare(b.shift ?? '') ||
        (a.section ?? '').localeCompare(b.section ?? '')
      );
    });

    return [...mappedSchoolClasses];
  }
  private getLessonPlanDownloadErrorStatus(
    lessonPlanDetail: lessonPlanDetailRecord,
    gradeSubjectId: number,
  ) {
    analyticsService.trackEvent(EventNames.lessonDownloadError, {
      epLpId: lessonPlanDetail.id,
      epGradesubjectId: gradeSubjectId,
      epLpDownloadErrorMessage: lessonPlanDetail?.downloadErrorCode ?? '',
    });
  }
  private getLessonPlanDownloadCompleteStatus(
    lessonPlanDetail: lessonPlanDetailRecord,
    gradeSubjectId: number,
    lpChapterName: string,
  ) {
    analyticsService.trackEvent(EventNames.lessonDownloadComplete, {
      epGradesubjectId: gradeSubjectId,
      epChapterName: lpChapterName,
      epLpId: lessonPlanDetail.id,
    });
  }
}

export const lessonPlanDB = new LessonPlanDB();
